import React, { useEffect, useState } from 'react';

import { ConfigurationFeatureFlag } from '@tink/config/src/tinkLinkConfig';
import { LoadingOverlay } from '../LoadingOverlay/LoadingOverlay';
import {
  ApplicationResult,
  postNotificationHandler,
} from '../../../utilities/post-notification-handler';
import {
  Configuration,
  useConfiguration,
} from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { ApplicationEvent, PageView } from '../../../models/analytics';

import { parentMessenger } from '../../Analytics/ParentApplicationEventMessenger';
import { timerUtils, WaitTimer } from '../../../utilities/timer';
import { applicationParameters } from '../../../utilities/application-parameters';
import { SuccessOverlay } from './molecules/SuccessOverlay/SuccessOverlay';

export type SuccessNotificationProps = {
  overlayHeading?: JSX.Element;
  overlaySubtext?: React.ReactNode;
  applicationSuccessResult: ApplicationResult;
  forceSkipSuccessScreen?: boolean;
  waitTimer?: WaitTimer;
};

export const SuccessNotification: React.FC<SuccessNotificationProps> = ({
  applicationSuccessResult,
  overlayHeading,
  overlaySubtext,
  forceSkipSuccessScreen = false,
  waitTimer = timerUtils.waitTimer,
}) => {
  const [autoRedirectFailed, setAutoRedirectFailed] = useState<undefined | boolean>(undefined);
  const { tinklinkAnalytics } = useAnalytics();
  const { data: configurationData } = useConfiguration();
  const isIframe = applicationParameters.get('isIframe');

  const skipSuccessScreen =
    forceSkipSuccessScreen ||
    Configuration.hasFeatureFlag(
      configurationData,
      ConfigurationFeatureFlag.SKIP_FINAL_SUCCESS_SCREEN
    ) ||
    Configuration.hasFeatureFlag(
      configurationData,
      ConfigurationFeatureFlag.SKIP_UPDATING_CREDENTIALS
    );

  useEffect(() => {
    const initialize = async () => {
      await tinklinkAnalytics.sendViewEvent({ view: PageView.SUCCESS_SCREEN });
      parentMessenger.fireViewEvent(PageView.SUCCESS_SCREEN);

      if (!skipSuccessScreen) {
        await waitTimer(3000);
      }

      await tinklinkAnalytics.sendApplicationEventOnce(ApplicationEvent.REDIRECT_ATTEMPT);

      await tinklinkAnalytics.sendInteractionEventOnce({
        view: PageView.SUCCESS_SCREEN,
        label: applicationSuccessResult.buildQueryPath() as string,
        action: ApplicationEvent.REDIRECT_ATTEMPT,
      });

      postNotificationHandler.execute(applicationSuccessResult, tinklinkAnalytics);

      if (!isIframe) {
        await waitTimer(5000);

        setAutoRedirectFailed(true);
      }
    };

    initialize();
  }, [
    applicationSuccessResult,
    skipSuccessScreen,
    tinklinkAnalytics,
    setAutoRedirectFailed,
    waitTimer,
    isIframe,
  ]);

  if (skipSuccessScreen) {
    return <LoadingOverlay />;
  }

  return (
    <SuccessOverlay
      text={overlayHeading}
      autoRedirectFailed={autoRedirectFailed}
      redirectUri={postNotificationHandler.buildCustomerRedirectUrl(applicationSuccessResult)}
      subtext={overlaySubtext}
    />
  );
};
